import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  TextFieldProps,
  Checkbox,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../components/Inputs/admin/UserListDropdown';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import {
  currencyOptions,
  flexiblePlanExpiredInTypeOptions,
  ICurrencyOption,
  paymentAdjustmentTypeOptions,
  TFlexiblePlanExpiredInTypeOption,
  TPaymentAdjustmentTypeOption,
} from '../../../../../../constants';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import {
  capitalizeFirstLetter,
  getFormatedDurationByMinutes,
} from '../../../../../../helper';
import {
  adminCreatePaymentAdjustment,
  PAYMENT_ADJUSTMENT_TYPES,
  TAdminPaymentAdjustmentCreate,
} from '../../../../../../store/V2/SettingsSlice';
import { TFlexiblePlanOption } from '../../../../../../components/Inputs/admin/FlexiblePlanListDropDown';
import { useAppSelector } from '../../../../../../hooks';
import {
  adminGetUserFlexiblePlans,
  getFlexiblePlansState,
  resetAdminUserFlexiblePlans,
} from '../../../../../../store/V2/FlexiblePlansSlice';

interface AdjustmentsTabProps {
  onClose: () => void;
}

type TState = {
  selectedStudent: TUserSelectOption | null;
  selectedType: TPaymentAdjustmentTypeOption;
  selectedFlexiblePlan: TFlexiblePlanOption | null;
  description: string;
  isCustomPlanChecked: boolean;
  isFlexiblePlanChecked: boolean;
  numberOfHours: string;
  rate: string;
  selectedCurrency: ICurrencyOption | null;
  expiredInCount: string;
  expiredInType: TFlexiblePlanExpiredInTypeOption;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  selectedStudent: null,
  selectedType: paymentAdjustmentTypeOptions[0],
  selectedFlexiblePlan: null,
  description: '',
  isCustomPlanChecked: false,
  isFlexiblePlanChecked: false,
  numberOfHours: '',
  rate: '',
  selectedCurrency: null,
  expiredInCount: '1',
  expiredInType: flexiblePlanExpiredInTypeOptions[3],
};

const AdjustmentsTab: FunctionComponent<AdjustmentsTabProps> = ({
  onClose,
}) => {
  const dispatch = useDispatch();

  // Store
  const flexiblePlansState = useAppSelector(getFlexiblePlansState);

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const flexiblePlanOptions: TFlexiblePlanOption[] = [];
  const isSubmitDisabled = Boolean(
    !state.selectedStudent ||
      !state.selectedType ||
      (state.selectedType &&
        state.selectedType.value === PAYMENT_ADJUSTMENT_TYPES.PAYMENT &&
        state.isFlexiblePlanChecked &&
        !state.selectedFlexiblePlan) ||
      (state.selectedType &&
        state.selectedType.value === PAYMENT_ADJUSTMENT_TYPES.PAYMENT &&
        state.isCustomPlanChecked &&
        (!state.rate.trim().length ||
          parseFloat(state.rate) < 1 ||
          !state.numberOfHours.trim().length ||
          parseFloat(state.numberOfHours) < 1 ||
          !state.selectedCurrency)) ||
      (state.selectedType &&
        state.selectedType.value !== PAYMENT_ADJUSTMENT_TYPES.PAYMENT &&
        (!state.rate.trim().length ||
          !state.numberOfHours.trim().length ||
          !state.selectedCurrency)) ||
      (state.selectedType &&
        ((state.selectedType.value === PAYMENT_ADJUSTMENT_TYPES.PAYMENT &&
          state.isCustomPlanChecked) ||
          state.selectedType.value === PAYMENT_ADJUSTMENT_TYPES.OTHERS) &&
        !state.expiredInCount.trim().length) ||
      parseFloat(state.expiredInCount) < 1 ||
      !state.expiredInType
  );

  if (
    flexiblePlansState.adminUserFlexiblePlans &&
    flexiblePlansState.adminUserFlexiblePlans.length
  ) {
    flexiblePlansState.adminUserFlexiblePlans.forEach((item) => {
      flexiblePlanOptions.push({
        label: item.name,
        value: item._id,
        credits: item.numberOfCredits,
      });
    });
  }

  useEffect(() => {
    return () => {
      dispatch(resetAdminUserFlexiblePlans());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const resetForm = () => {
    dispatch(resetAdminUserFlexiblePlans());

    setState({ ...initialState });
  };

  const handleStudentChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TUserSelectOption | null
  ) => {
    dispatch(resetAdminUserFlexiblePlans());

    if (value) {
      dispatch(
        adminGetUserFlexiblePlans({
          userId: value.value,
        })
      );
    }

    setState((prevState) => ({
      ...prevState,
      selectedStudent: value,
      selectedFlexiblePlan: null,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (state.selectedStudent && state.selectedType) {
      const apiData: TAdminPaymentAdjustmentCreate = {
        userId: state.selectedStudent.value,
        type: state.selectedType.value,
      };

      if (
        state.selectedType &&
        state.selectedType.value === PAYMENT_ADJUSTMENT_TYPES.PAYMENT
      ) {
        if (state.isFlexiblePlanChecked && state.selectedFlexiblePlan) {
          apiData.flexiblePlanId = state.selectedFlexiblePlan.value;
        }

        if (
          state.isCustomPlanChecked &&
          state.numberOfHours &&
          state.rate &&
          state.selectedCurrency &&
          state.expiredInCount &&
          state.expiredInType
        ) {
          apiData.numberOfHours = parseFloat(state.numberOfHours);
          apiData.rate = parseFloat(state.rate);
          apiData.currency = state.selectedCurrency.value;
        }
      }

      if (
        state.selectedType &&
        state.selectedType.value !== PAYMENT_ADJUSTMENT_TYPES.PAYMENT &&
        state.selectedCurrency
      ) {
        apiData.numberOfHours = parseFloat(state.numberOfHours);
        apiData.rate = parseFloat(state.rate);
        apiData.currency = state.selectedCurrency.value;
      }

      if (
        state.selectedType &&
        state.selectedType.value !== PAYMENT_ADJUSTMENT_TYPES.PAYMENT &&
        parseFloat(state.rate) > 0 &&
        state.expiredInCount &&
        state.expiredInType
      ) {
        apiData.expiredIn = {
          count: parseFloat(state.expiredInCount),
          type: state.expiredInType.value,
        };
      }

      if (state.description.trim().length) {
        apiData.description = state.description;
      }

      const res: any = await dispatch(adminCreatePaymentAdjustment(apiData));

      if (res.payload) {
        resetForm();
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Adjustments
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <UserListDropdown
                  selectedItem={state.selectedStudent}
                  handleChange={handleStudentChange}
                  textFieldProps={{
                    label: 'Student',
                    placeholder: 'Select',
                    ...textFieldProps,
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  disableClearable
                  size="small"
                  value={state.selectedType}
                  options={paymentAdjustmentTypeOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  onChange={(event, value) =>
                    updateStateAttributes({
                      selectedType: value,
                    })
                  }
                  getOptionLabel={(option) =>
                    capitalizeFirstLetter(option.label.toLowerCase())
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Type *"
                      placeholder="Select"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            {state.selectedStudent &&
            state.selectedType &&
            state.selectedType.value === PAYMENT_ADJUSTMENT_TYPES.PAYMENT ? (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormGroup>
                      <FormControlLabel
                        label="Custom Plan"
                        sx={{ width: 'fit-content' }}
                        control={
                          <Checkbox
                            checked={state.isCustomPlanChecked}
                            onChange={(event, value) =>
                              updateStateAttributes({
                                isCustomPlanChecked: value,
                                isFlexiblePlanChecked: false,
                              })
                            }
                          />
                        }
                      />
                      <FormControlLabel
                        label="Flexible Plan"
                        sx={{ width: 'fit-content' }}
                        control={
                          <Checkbox
                            checked={state.isFlexiblePlanChecked}
                            onChange={(event, value) =>
                              updateStateAttributes({
                                isFlexiblePlanChecked: value,
                                isCustomPlanChecked: false,
                              })
                            }
                          />
                        }
                      />
                    </FormGroup>
                  </FormControl>
                </Grid>
                {state.isFlexiblePlanChecked ? (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <Autocomplete
                        fullWidth
                        size="small"
                        value={state.selectedFlexiblePlan}
                        options={flexiblePlanOptions}
                        popupIcon={TPAutoCompleteArrowIconComponent}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            selectedFlexiblePlan: value,
                          })
                        }
                        getOptionLabel={(option: TFlexiblePlanOption) =>
                          `${option.label}${
                            option.credits
                              ? ` - ${getFormatedDurationByMinutes({
                                  minutes: option.credits,
                                })}`
                              : ''
                          }`
                        }
                        renderInput={(params) => (
                          <TPTextField
                            {...params}
                            {...textFieldProps}
                            size="small"
                            label="Flexible Plan *"
                            placeholder="Select"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                ) : null}
              </>
            ) : null}
            {state.isCustomPlanChecked ||
            (state.selectedType &&
              state.selectedType.value !== PAYMENT_ADJUSTMENT_TYPES.PAYMENT) ? (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TPTextField
                      {...textFieldProps}
                      type="number"
                      label="No of Hours *"
                      placeholder="Enter here..."
                      value={state.numberOfHours}
                      onChange={(event) =>
                        updateStateAttributes({
                          numberOfHours: event.target.value,
                        })
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TPTextField
                      {...textFieldProps}
                      type="number"
                      label="Hourly Rate *"
                      placeholder="Enter here..."
                      value={state.rate}
                      onChange={(event) =>
                        updateStateAttributes({ rate: event.target.value })
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      size="small"
                      value={state.selectedCurrency}
                      options={currencyOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      onChange={(event, value) =>
                        updateStateAttributes({
                          selectedCurrency: value,
                        })
                      }
                      getOptionLabel={(option) =>
                        capitalizeFirstLetter(option.label)
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          label="Currency *"
                          placeholder="Select"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </>
            ) : null}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  multiline
                  rows={3}
                  label="Description"
                  placeholder="Enter here..."
                  value={state.description}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) =>
                    updateStateAttributes({ description: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            {(state.selectedType &&
              ((state.selectedType.value === PAYMENT_ADJUSTMENT_TYPES.PAYMENT &&
                state.isCustomPlanChecked) ||
                state.selectedType.value ===
                  PAYMENT_ADJUSTMENT_TYPES.OTHERS)) ||
            (state.selectedType.value !== PAYMENT_ADJUSTMENT_TYPES.PAYMENT &&
              parseFloat(state.rate) < 0) ? (
              <>
                <Grid item xs={12}>
                  <Typography fontSize="14px" fontWeight={600}>
                    Expired In *
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <TPTextField
                      {...textFieldProps}
                      type="number"
                      placeholder="Enter here..."
                      value={state.expiredInCount}
                      onChange={(event) =>
                        updateStateAttributes({
                          expiredInCount: event.target.value,
                        })
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      disableClearable
                      fullWidth
                      size="small"
                      value={state.expiredInType}
                      options={flexiblePlanExpiredInTypeOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      onChange={(event, value) =>
                        updateStateAttributes({ expiredInType: value })
                      }
                      getOptionLabel={(option) =>
                        capitalizeFirstLetter(option.label.toLocaleLowerCase())
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          placeholder="Type"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </>
            ) : null}
          </Grid>
        </div>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <DarkLoadingButton
          disableElevation
          variant="contained"
          color="inherit"
          loading={isLoading}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{ ...defaultButtonStyles }}
        >
          Add
        </DarkLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AdjustmentsTab;
